import React, { useCallback, useEffect } from "react";
import { TextField, Button, Checkbox, FormControlLabel } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import { useDebounce } from "../utils/hooks";
import colors from "../constants/colors";
import { SubscriptionFilters } from "../models/Subscription";

export interface SubscriptionFilterProps {
	filters: SubscriptionFilters;
	onFilter: (filters: SubscriptionFilters) => void;
}

const styles = {
	wrapper: {
		display: "flex",
		maxWidth: 960,
		marginBottom: 24,
	},
	textField: {
		width: "100%",
	},
	textFieldLabel: {
		fontSize: 14,
		letterSpacing: "0.1em",
		margin: 0,
		textTransform: "uppercase" as any,
		color: colors.gray600,
	},
	searchButton: {
		marginLeft: 8,
		padding: "0 22px",
	},
	showExpiredCheckbox: {
		width: "250px",
		marginLeft: "12px",
	},
};

export const SubscriptionFilter = ({ filters, onFilter }: SubscriptionFilterProps) => {
	const [companyFilterInput, setCompanyFilterInput] = React.useState(filters.companyName);
	const [showExpiredFilter, setShowExpiredFilter] = React.useState(filters.showExpired);

	const debouncedInputFieldValue = useDebounce(companyFilterInput, 500);

	const setFilters = useCallback(
		(companyFilter: string, showExpiredFilter: boolean) => {
			const filters: SubscriptionFilters = { companyName: companyFilter.trim(), showExpired: showExpiredFilter };
			onFilter(filters);
		},
		[onFilter],
	);

	useEffect(() => {
		// searches when 3 characters in textfield or textfield cleared
		if (debouncedInputFieldValue.length >= 3 || debouncedInputFieldValue.length < 1) {
			setFilters(debouncedInputFieldValue, showExpiredFilter);
		}
	}, [debouncedInputFieldValue, setFilters, showExpiredFilter]);

	useEffect(() => {
		setFilters(companyFilterInput, showExpiredFilter);
	}, [companyFilterInput, setFilters, showExpiredFilter]);

	return (
		<div style={styles.wrapper} data-testid="subscription-filter-wrapper">
			<TextField
				inputProps={{ "data-testid": "company-name-input" }}
				type="text"
				value={companyFilterInput}
				style={styles.textField}
				label={<span style={styles.textFieldLabel}>Search by company</span>}
				InputLabelProps={{ shrink: true }}
				placeholder="e.g. twilio mso"
				onChange={(e) => {
					setCompanyFilterInput(e.target.value);
				}}
			/>

			<FormControlLabel
				style={styles.showExpiredCheckbox}
				control={
					// ts-ignore is used because material ui does not allow data-testid on input props currently
					<Checkbox
						inputProps={{
							// @ts-expect-error MUI types don't allow for data attributes
							"data-testid": "show-expired-checkbox",
						}}
						checked={showExpiredFilter}
						onChange={(checkbox) => {
							setShowExpiredFilter(checkbox.target.checked);
						}}
						color="primary"
					/>
				}
				label="Show Expired"
			/>

			<Button
				data-testid="company-name-filter-btn"
				style={styles.searchButton}
				startIcon={<SearchIcon />}
				onClick={() => {
					setFilters(companyFilterInput, showExpiredFilter);
				}}
			>
				Search
			</Button>
		</div>
	);
};
